import { useMemo, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import CurrencyInputField from 'react-currency-input-field'
import { Trans, useTranslation } from 'react-i18next'
import classNames from 'classnames/bind'

import { AcknowledgeModal, ActionModal, FormField, Flex, Modal, Select, Checkbox, Typography } from '@percent/lemonade'
import { useCurrencies } from '@percent/admin-dashboard/common/hooks'
import { MarkAsPaidModalProps } from './MarkAsPaidModal.types'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { formatAmountFromMinorUnits } from '@percent/admin-dashboard/common/utility/formatAmount/formatAmount'
import { getCurrencyMinorUnits } from '@percent/admin-dashboard/common/utility/getCurrencyMinorUnits'
import styles from './MarkAsPaidModal.module.scss'

const cx = classNames.bind(styles)

const getAmountValueFromMinorUnits = ({
  value,
  currencyCode,
  currencies
}: {
  value: number
  currencyCode: string
  currencies: Parameters<typeof getCurrencyMinorUnits>[1]
}): string => {
  const minorUnits = getCurrencyMinorUnits(currencyCode, currencies)
  const divisor = 10 ** minorUnits
  const normalizedValue = value / divisor

  if (!Number.isFinite(normalizedValue)) {
    return ''
  }

  if (minorUnits === 0) {
    return `${Math.round(normalizedValue)}`
  }

  return normalizedValue.toFixed(minorUnits)
}

export function MarkAsPaidModal({ isOpen, onClose, disbursementDetails, refreshData }: MarkAsPaidModalProps) {
  const { t, i18n } = useTranslation()
  const { currencyInfo } = useCurrencies()
  const { disbursementsService } = useServices()
  const intlConfigLang = i18n.language

  const [modalState, setModalState] = useState<'form' | 'success' | 'error'>('form')
  const [isOverwrite, setIsOverwrite] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const currencyOptions = useMemo(() => {
    return currencyInfo
      ?.map(currency => ({
        label: `${currency.name} (${currency.code})`,
        value: currency.code
      }))
      .sort((a, b) => a.value.localeCompare(b.value))
  }, [currencyInfo])

  const { errors, values, setFieldValue, handleSubmit, resetForm, isValid, handleBlur, touched } = useFormik({
    initialValues: {
      amount: getAmountValueFromMinorUnits({
        currencyCode: disbursementDetails.foundationCurrencyCode,
        value: disbursementDetails.foundationAmount,
        currencies: currencyInfo
      }),
      currency: disbursementDetails.foundationCurrencyCode
    },
    validationSchema: () =>
      Yup.object().shape({
        amount: Yup.string()
          .trim()
          .required('Required')
          .matches(
            getCurrencyMinorUnits(currency, currencyInfo) === 0
              ? /^[0-9]{1,11}?$/
              : new RegExp(`^[0-9]{1,11}(?:.[0-9]{1,${getCurrencyMinorUnits(currency, currencyInfo)}})?$`),
            `The maximum number of decimals is ${getCurrencyMinorUnits(currency, currencyInfo)}`
          ),
        currency: Yup.string().required('Required')
      }),
    onSubmit: async ({ amount, currency }) => {
      try {
        setIsLoading(true)
        await disbursementsService.markDisbursementAsPaid({
          id: disbursementDetails?.id || '',
          paidAt: dayJS().toISOString(),
          paidAmount: Math.round(parseFloat(amount) * 10 ** getCurrencyMinorUnits(currency, currencyInfo)),
          paidCurrency: currency
        })
        setModalState('success')
      } catch {
        setModalState('error')
      } finally {
        setIsLoading(false)
      }
    }
  })

  const handleClose = () => {
    resetForm()
    setModalState('form')
    onClose()
  }

  const handleSuccess = () => {
    refreshData?.()
    handleClose()
  }

  const { amount, currency } = values
  const confirmationAmount = useMemo(() => {
    const minorUnits = getCurrencyMinorUnits(currency, currencyInfo)
    const parsedAmount = Number(amount)

    if (!Number.isFinite(parsedAmount)) {
      return ''
    }

    return formatAmountFromMinorUnits({
      currencyCode: currency,
      value: Math.round(parsedAmount * 10 ** minorUnits),
      currencies: currencyInfo
    })
  }, [amount, currency, currencyInfo])

  const defaultCurrencyOption = useMemo(() => {
    return currencyOptions?.find(option => option.value === currency)
  }, [currencyOptions, currency])

  const successModal = modalState === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.markAsPaid.success')}
      buttonText={t('button.done')}
      handleClose={handleSuccess}
    />
  )

  const errorModal = modalState === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.markAsPaid.error')}
      buttonText={t('button.done')}
      handleClose={handleClose}
    />
  )

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby="mark-as-paid-modal">
      {successModal || errorModal || (
        <ActionModal
          title={t('dialog.markAsPaid.header')}
          primaryButtonText={t('button.confirm')}
          secondaryButtonText={t('button.cancel')}
          disabled={!(isValid && !isLoading)}
          loadingText={t('button.confirming')}
          loading={isLoading}
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          primaryBtnTestId="confirm-button"
          secondaryBtnTestId="cancel-button"
        >
          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="md">
              <Flex gap="md">
                <div className={styles.fieldWrapper}>
                  <FormField
                    label={t('dialog.markAsPaid.form.amount.label')}
                    data-testid="amount"
                    status={errors.amount && touched.amount ? 'danger' : 'default'}
                    statusMessage={errors.amount}
                    disabled={!isOverwrite}
                  >
                    <CurrencyInputField
                      id="amount"
                      name="amount"
                      value={amount}
                      onValueChange={value => setFieldValue('amount', value?.replace(/-/g, ''))}
                      onBlur={handleBlur}
                      placeholder={t('dialog.markAsPaid.form.amount.placeholder')}
                      data-testid="amount"
                      intlConfig={{
                        locale: currency === 'USD' ? 'en-US' : intlConfigLang,
                        currency
                      }}
                      disableAbbreviations
                      className={cx({
                        currencyInputWrapper: true,
                        danger: touched.amount && errors.amount
                      })}
                    />
                  </FormField>
                </div>
                <div className={styles.fieldWrapper}>
                  {currencyOptions && (
                    <FormField
                      label={t('dialog.markAsPaid.form.currency.label')}
                      data-testid="select-currency"
                      status={errors.currency && touched.currency ? 'danger' : 'default'}
                      statusMessage={errors.currency}
                      disabled={!isOverwrite}
                    >
                      <Select
                        placeholder={t('dialog.markAsPaid.form.currency.placeholder')}
                        defaultValue={defaultCurrencyOption}
                        searchable
                        options={currencyOptions}
                        onChange={event => {
                          setFieldValue('currency', event?.value ?? '')
                        }}
                      />
                    </FormField>
                  )}
                </div>
              </Flex>
              <Checkbox
                variant="default"
                name="overwrite"
                label={t('dialog.markAsPaid.overwrite')}
                onChange={() => setIsOverwrite(!isOverwrite)}
                active={isOverwrite}
              />
              {disbursementDetails && (
                <Typography variant="default" $color="gray600">
                  <Trans
                    i18nKey="dialog.markAsPaid.confirmation"
                    values={{
                      amount: `${confirmationAmount} ${currency}`,
                      organisationName: disbursementDetails.organisationName
                    }}
                    components={{ b: <span style={{ fontWeight: 500 }} /> }}
                  />
                </Typography>
              )}
            </Flex>
          </form>
        </ActionModal>
      )}
    </Modal>
  )
}
